import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  acknowledgeIssue,
  completeIssue,
  getIssueDetail,
  type AcknowledgeIssueRequest,
  type CompleteIssueRequest,
} from '../api/issueDetailApi'

// One queryOptions factory per resource — route loader + page subscription
// share the cache key so prefetch + render hit the same store.
export const issueDetailQueryOptions = (issueId: string | undefined) =>
  queryOptions({
    queryKey: ['issue', issueId] as const,
    queryFn: () => getIssueDetail(issueId as string),
    enabled: Boolean(issueId),
    staleTime: 1000 * 30,
  })

export function useIssueDetail(issueId: string | undefined) {
  return useQuery(issueDetailQueryOptions(issueId))
}

// ── Lifecycle v2 mutations — acknowledge (open → pending) and complete
// (pending → completed). Both invalidate rather than hand-merge the cache:
// the responses are event stubs, not full issues, and the status flip
// ripples through per-bucket counts + per-site rollups that only the
// server can recompute.
//
// Invalidation wave (coarse prefixes — every params tuple under them):
//   ['issue', id]      — this detail page (status, timeline, lifecycle strip)
//   ['hod-issues']     — HOD inbox buckets + bucket_counts + analytics
//   ['hod-sites']      — per-site issue_counts on My Sites / dashboard
//   ['hod-site-detail']— site drill-down issue panel
//   ['issues'] / ['entity-issues'] / ['site-issues'] — admin list surfaces
//   ['site-detail'] / ['entity-detail'] — embedded issue stats
function invalidateIssueCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  issueId: string,
) {
  void queryClient.invalidateQueries({ queryKey: ['issue', issueId] })
  void queryClient.invalidateQueries({ queryKey: ['hod-issues'] })
  void queryClient.invalidateQueries({ queryKey: ['hod-sites'] })
  void queryClient.invalidateQueries({ queryKey: ['hod-site-detail'] })
  void queryClient.invalidateQueries({ queryKey: ['issues'] })
  void queryClient.invalidateQueries({ queryKey: ['entity-issues'] })
  void queryClient.invalidateQueries({ queryKey: ['site-issues'] })
  void queryClient.invalidateQueries({ queryKey: ['site-detail'] })
  void queryClient.invalidateQueries({ queryKey: ['entity-detail'] })
}

export function useAcknowledgeIssue(issueId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: AcknowledgeIssueRequest) => acknowledgeIssue(issueId, body),
    onSuccess: () => invalidateIssueCaches(queryClient, issueId),
  })
}

export function useCompleteIssue(issueId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CompleteIssueRequest) => completeIssue(issueId, body),
    onSuccess: () => invalidateIssueCaches(queryClient, issueId),
  })
}
